// ============================================================================
// Password-reset links: remember them from the very first line of the app.
// ----------------------------------------------------------------------------
// The e-mailed link lands on /auth#access_token=…&type=recovery. supabase-js
// reads that hash when the client is created, strips it from the address bar
// and fires PASSWORD_RECOVERY — all before the Auth page (a lazily loaded
// chunk) has mounted. The page then saw neither the hash nor the event and
// showed the plain login form, so the link "did nothing".
//
// lib/supabase.js calls captureRecoveryFromUrl() BEFORE creating the client and
// markRecovery() on PASSWORD_RECOVERY; Auth.jsx asks isRecoveryPending() when it
// mounts and clearRecovery() once the new password is saved. The mark lives in
// sessionStorage (this tab only) and expires with the recovery session (1 h).
// ============================================================================
const KEY = 'zb_password_recovery';
const TTL_MS = 60 * 60 * 1000;
let memory = 0; // fallback when sessionStorage is unavailable

function storage() {
  try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch { return null; }
}

export function markRecovery(now = Date.now()) {
  memory = now;
  try { storage()?.setItem(KEY, String(now)); } catch { /* private mode */ }
}

export function clearRecovery() {
  memory = 0;
  try { storage()?.removeItem(KEY); } catch { /* private mode */ }
}

export function isRecoveryPending(now = Date.now()) {
  let at = memory;
  try { at = Number(storage()?.getItem(KEY)) || at; } catch { /* private mode */ }
  if (!at) return false;
  if (now - at > TTL_MS) { clearRecovery(); return false; }
  return true;
}

// True when the address carries a recovery link: the implicit flow puts
// type=recovery in the hash, some templates add it to the query string.
export function isRecoveryUrl(loc) {
  if (!loc) return false;
  const hash = new URLSearchParams(String(loc.hash || '').replace(/^#/, ''));
  if (hash.get('error_code')) return false;
  if (hash.get('type') === 'recovery') return true;
  return new URLSearchParams(String(loc.search || '')).get('type') === 'recovery';
}

export function captureRecoveryFromUrl(loc = typeof window !== 'undefined' ? window.location : null) {
  if (isRecoveryUrl(loc)) { markRecovery(); return true; }
  return false;
}
